import { Divider, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { AllocationRuleDto } from '@yard/app/api'
import { RuleCapacity } from '@yard/utils/allocation-rule-capacity.util'
import { observer } from 'mobx-react-lite'
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'
import { AllocationRuleAccordionWrapper } from '../../atoms/AllocationRuleAccordionWrapper'
import { AssignedDraggableAllocationRuleDraggable } from '../../molecules/AssignedAllocationRuleDraggable'

interface Props {
  droppableId: string
  title: string
  tooltip?: string
  allocationRules: AllocationRuleDto[]
  capacityByRule?: Record<string, RuleCapacity>
  matchingCountByRule?: Record<string, number>
  hoveredIndex?: number
  isEditable?: boolean
  onHover: (index?: number) => void
  onReorder?: (sourceIndex: number, destinationIndex: number) => void
  onEdit?: (index: number) => void
  onDelete?: (index: number) => void
}
export const AllocationRuleAccordion = observer((props: Props) => {
  const {
    droppableId,
    title,
    tooltip,
    allocationRules,
    capacityByRule,
    matchingCountByRule,
    hoveredIndex,
    isEditable,
    onReorder,
    onHover,
    onEdit,
    onDelete,
  } = props
  const { t } = useTranslate()

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return

    onReorder?.(result.source.index, result.destination.index)
  }

  return (
    <AllocationRuleAccordionWrapper title={title} tooltip={tooltip}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={droppableId} isDropDisabled={!isEditable}>
          {provided => (
            <Stack
              ref={provided.innerRef}
              {...provided.droppableProps}
              divider={<Divider />}
              sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                maxHeight: '35vh',
              }}
            >
              {allocationRules.map((allocationRule, index) => (
                <AssignedDraggableAllocationRuleDraggable
                  key={`allocation-rule-${index}`}
                  allocationRule={allocationRule}
                  capacityByRule={capacityByRule}
                  matchingCountByRule={matchingCountByRule}
                  index={index}
                  isEditable={isEditable}
                  isSelected={hoveredIndex === index}
                  onMouseOver={() => onHover(index)}
                  onMouseLeave={() => onHover()}
                  onEdit={() => onEdit?.(index)}
                  onDelete={() => onDelete?.(index)}
                />
              ))}
              {provided.placeholder}

              {allocationRules.length === 0 && (
                <Stack alignItems='center' padding={2}>
                  <Typography variant='body2' color='text.secondary'>
                    {t(
                      'noAllocationRulesHasBeenCreatedYet',
                      'No allocation rules have been created yet',
                    )}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    </AllocationRuleAccordionWrapper>
  )
})
